/**
 * Player names for online play (docs/ONLINE.md, "Players and names"; owner, 2026-10-24): two
 * picks from these lists and a number, like "SPEEDY SQUIRREL 42". No typing, so nothing to
 * moderate, and the age rating keeps "no user-generated content".
 *
 * A name is stored as indexes into these lists, so:
 * - **never reorder or reuse an index.** New words go on the end;
 * - **to retire a word, replace it with `null`** (names using it then read as the fallback, and it
 *   is never offered again).
 *
 * Words are family-friendly and lawn, garden and critter themed. Left out on purpose: anything
 * with a rude second meaning (some birds and critters among them), slang for drugs, words about
 * bodies, size or smarts, and colors that could read as being about people.
 */

/** At most 6 letters, so a name fits a board row (see NAME_MAX_LENGTH). */
export const NAME_ADJECTIVES: readonly (string | null)[] = [
  'SUNNY',
  'SPEEDY',
  'BREEZY',
  'TIDY',
  'MOSSY',
  'LEAFY',
  'ZIPPY',
  'JOLLY',
  'LUCKY',
  'PLUCKY',
  'MERRY',
  'SNAPPY',
  'BOUNCY',
  'CLEVER',
  'COZY',
  'DAPPER',
  'DEWY',
  'EAGER',
  'FANCY',
  'FLUFFY',
  'FROSTY',
  'GENTLE',
  'GOLDEN',
  'GRASSY',
  'HAPPY',
  'HUMBLE',
  'JAZZY',
  'KEEN',
  'LIVELY',
  'MELLOW',
  'MIGHTY',
  'MINTY',
  'MISTY',
  'NIFTY',
  'PEPPY',
  'PERKY',
  'QUICK',
  'QUIET',
  'RAPID',
  'ROSY',
  'RUSTY',
  'SHINY',
  'SPARKY',
  'SPIFFY',
  'SPRY',
  'STEADY',
  'STURDY',
  'SWIFT',
  'TRUSTY',
  'BRAVE',
  'BRIGHT',
  'CALM',
  'CHEERY',
  'CRISP',
  'DARING',
  'DANDY',
  'NOBLE',
  'PROUD',
  'SNUG',
  'SPUNKY',
  'WITTY',
  'ZESTY',
  'WISE',
  'CHIRPY',
];

/** At most 8 letters. */
export const NAME_NOUNS: readonly (string | null)[] = [
  'SQUIRREL',
  'GOPHER',
  'HEDGEHOG',
  'CHIPMUNK',
  'BADGER',
  'OTTER',
  'RABBIT',
  'BUNNY',
  'HARE',
  'FOX',
  'MOLE',
  'TOAD',
  'FROG',
  'BEETLE',
  'CRICKET',
  'LADYBUG',
  'BEE',
  'SNAIL',
  'MOTH',
  'RACCOON',
  'POSSUM',
  'OWL',
  'HAWK',
  'HERON',
  'SPARROW',
  'ROBIN',
  'FINCH',
  'WREN',
  'DUCK',
  'GOOSE',
  'LAMB',
  'PONY',
  'PUPPY',
  'KITTEN',
  'ACORN',
  'CLOVER',
  'DAISY',
  'TULIP',
  'FERN',
  'MAPLE',
  'OAK',
  'PINE',
  'WILLOW',
  'THISTLE',
  'PEBBLE',
  'MEADOW',
  'GARDEN',
  'HEDGE',
  'SPROUT',
  'SEEDLING',
  'MOWER',
  'RAKE',
  'TROWEL',
  'SHOVEL',
  'WAGON',
  'TRACTOR',
  'BARROW',
  'GNOME',
  'PUMPKIN',
  'TURNIP',
  'RADISH',
  'CARROT',
  'MUSHROOM',
  'BLOSSOM',
];

/** Numbers never given or accepted: they carry hateful or rude meanings online. */
export const BANNED_NAME_NUMBERS: ReadonlySet<number> = new Set([14, 18, 69, 88]);

/** The longest a name can be ("SPEEDY SQUIRREL 42"): 6 + 1 + 8 + 1 + 2. */
export const NAME_MAX_LENGTH = 18;

/** A name made of words: indexes into the two lists, and a number from 0 to 99. */
export interface WordName {
  kind: 'words';
  parts: [number, number];
  number: number;
}

const word = (list: readonly (string | null)[], i: number) =>
  Number.isInteger(i) ? (list[i] ?? null) : null;

/** Whether the game can make this name today: both words in use, the number allowed. */
export function isValidName(name: WordName): boolean {
  return (
    word(NAME_ADJECTIVES, name.parts[0]) !== null &&
    word(NAME_NOUNS, name.parts[1]) !== null &&
    Number.isInteger(name.number) &&
    name.number >= 0 &&
    name.number <= 99 &&
    !BANNED_NAME_NUMBERS.has(name.number)
  );
}

/** How a name reads, e.g. "SPEEDY SQUIRREL 42". A retired word reads as "MOWER". */
export function nameText(name: WordName): string {
  const adjective = word(NAME_ADJECTIVES, name.parts[0]);
  const noun = word(NAME_NOUNS, name.parts[1]) ?? 'MOWER';
  return [adjective, noun, String(name.number)].filter(Boolean).join(' ');
}

/** Indexes still in use in a list. */
const inUse = (list: readonly (string | null)[]) => list.flatMap((w, i) => (w === null ? [] : [i]));

/**
 * A random name, from three random numbers in [0, 1) (the caller picks the source: the server
 * uses crypto, the game's shuffle button Math.random).
 */
export function randomName(r1: number, r2: number, r3: number): WordName {
  const adjectives = inUse(NAME_ADJECTIVES);
  const nouns = inUse(NAME_NOUNS);
  const numbers = Array.from({ length: 100 }, (_, n) => n).filter(
    (n) => !BANNED_NAME_NUMBERS.has(n),
  );
  const pick = <T>(list: readonly T[], r: number): T => list[Math.floor(r * list.length)]!;
  return {
    kind: 'words',
    parts: [pick(adjectives, r1), pick(nouns, r2)],
    number: pick(numbers, r3),
  };
}

/**
 * The name with one part stepped to the next (or previous, `step` -1) choice, wrapping round:
 * part 0 the adjective, 1 the noun, 2 the number. Retired words and banned numbers are skipped.
 */
export function stepName(name: WordName, part: 0 | 1 | 2, step: 1 | -1): WordName {
  if (part === 2) {
    let n = name.number;
    do n = (n + step + 100) % 100;
    while (BANNED_NAME_NUMBERS.has(n));
    return { ...name, number: n };
  }
  const list = part === 0 ? NAME_ADJECTIVES : NAME_NOUNS;
  const open = inUse(list);
  const at = open.indexOf(name.parts[part]);
  const next = open[(at + step + open.length) % open.length]!;
  const parts: [number, number] = [...name.parts];
  parts[part] = next;
  return { ...name, parts };
}
